import {
  Button,
  DialogFooter,
  DialogHeader,
  DialogScrollableContent,
  DialogTitle,
} from "@win-reality/react-ui";
import { useState } from "react";

import { ThemedDialog } from "./ThemedDialog";

type EndGameDialogProps = {
  isOpen: boolean;
  onClose: () => void;
  /** Resolves to whether the game ended; the dialog stays open if not */
  onConfirm: () => Promise<boolean>;
  /** Ending saves the hitter and pitcher on the last plate appearance's pitches */
  isFillingLastPitches: boolean;
};

/** End Game confirmation, worded like the V1 tagger's End Session modal. */
export const EndGameDialog = ({
  isOpen,
  onClose,
  onConfirm,
  isFillingLastPitches,
}: EndGameDialogProps) => {
  const [isEnding, setIsEnding] = useState(false);

  const handleConfirm = async () => {
    setIsEnding(true);
    const isEnded = await onConfirm();
    setIsEnding(false);
    if (isEnded) onClose();
  };

  return (
    <ThemedDialog
      open={isOpen}
      onOpenChange={(open) => !open && !isEnding && onClose()}
      defaultCancelButton={false}
      size="md"
    >
      {({ showDivider, setScrollContainer }) => (
        <>
          <DialogScrollableContent setScrollContainer={setScrollContainer}>
            <DialogHeader>
              <DialogTitle className="text-gray-900 dark:text-white">
                End Game?
              </DialogTitle>
            </DialogHeader>
            <p
              className="text-tagging-muted"
              data-testid="end-game-status"
              data-status={isEnding ? "ending" : "idle"}
            >
              {/* Ending waits for the last at bat's pitches to get their
                  players, unless there are too many to fill */}
              {isEnding
                ? isFillingLastPitches
                  ? "Finalizing game… Saving the hitter and pitcher on the last pitches."
                  : "Finalizing game…"
                : "Are you sure you want to end the current game? This action cannot be undone."}
            </p>
          </DialogScrollableContent>

          <DialogFooter showDivider={showDivider}>
            <div className="flex w-full justify-between">
              <Button
                variant="ghost"
                type="button"
                className="underline"
                onClick={onClose}
                isDisabled={isEnding}
                data-testid="end-game-dialog-cancel-button"
              >
                Cancel
              </Button>
              <Button
                variant="secondary-flat-light"
                onClick={handleConfirm}
                isDisabled={isEnding}
                isLoading={isEnding}
                aria-busy={isEnding}
                data-testid="end-game-dialog-confirm-button"
              >
                End Game
              </Button>
            </div>
          </DialogFooter>
        </>
      )}
    </ThemedDialog>
  );
};
